import { css } from "@emotion/css";
import type { PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { ExternalLink, SectionHeader, Callout, Button, List, ListItemContent, ListItemLink, ListTitle, SimpleMenu, useMenuState } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { AccountResource, EnvironmentV2Resource, TenantResource, Repository, AzureSubscriptionAccountResource } from "@octopusdeploy/octopus-server-client";
import { AccountType, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links } from "@octopusdeploy/portal-routes";
import { sortBy, uniq } from "lodash";
import type * as React from "react";
import { createAddAccountAnalyticsEvent } from "~/areas/infrastructure/components/AccountEdit/amplitudeAnalytics";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { environmentV2ChipListIncludingMissing, tenantChipListIncludingMissing } from "../../../../components/Chips/index";
import { RawAccountTypeDetailsMap } from "../../InfrastructureDetails";
import styles from "./style.module.less";
interface AccountsPageProps {
    spaceId: string;
    loaderData: AccountsPageLoaderData;
}
interface AccountsPageLoaderData {
    accounts: AccountResource[];
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
}
export async function accountsPageLoader(repository: Repository, ephemeralEnvironmentsAreEnabled: boolean): Promise<AccountsPageLoaderData> {
    const accounts = await repository.Accounts.all();
    const environmentIds = uniq(accounts.reduce<string[]>((list, acc) => list.concat(acc.EnvironmentIds), []));
    const tenantIds = uniq(accounts.reduce<string[]>((list, acc) => list.concat(acc.TenantIds), []));
    const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all({ ids: tenantIds }) : Promise.resolve([]);
    const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository, environmentIds);
    return { accounts: accounts, environments: environments, tenants: await tenantsPromise };
}
export function AccountsPage({ spaceId, loaderData }: AccountsPageProps) {
    const Title = "Accounts";
    const { accounts, environments, tenants } = loaderData;
    const addAccountPageAction: PrimaryPageAction = {
        type: "custom",
        key: "Add Account",
        content: <AddAccountButton spaceId={spaceId}/>,
        hasPermissions: isAllowed({
            permission: Permission.AccountCreate,
            wildcard: true,
        }),
    };
    if (accounts.length === 0) {
        return (<PortalEmptyStateOnboarding title={"Add accounts to use during your deployments"} description={"Accounts help you to centralise account details used during your deployments, including things like username/password, tokens, Azure and AWS credentials and SSH key pairs."} primaryAction={addAccountPageAction} learnMore={<ExternalLink href="OnboardingAccountsLearnMore">Learn more</ExternalLink>}/>);
    }
    return (<PageContent header={{ title: Title, primaryAction: addAccountPageAction }}>
            <BorderedContent>
                {RawAccountTypeDetailsMap.map((accountTypeDetail) => (<AccountGroup key={accountTypeDetail.name} spaceId={spaceId} accounts={accounts} environments={environments} tenants={tenants} accountTypes={accountTypeDetail.types} groupName={accountTypeDetail.namePlural}/>))}
            </BorderedContent>
        </PageContent>);
}
interface AccountGroupProps {
    spaceId: string;
    accounts: AccountResource[];
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    accountTypes: AccountType[];
    groupName: string;
}
function AccountGroup({ spaceId, accounts, environments, tenants, accountTypes, groupName }: AccountGroupProps) {
    const filteredAccounts = accounts.filter((account) => accountTypes.indexOf(account.AccountType) !== -1);
    if (!filteredAccounts || filteredAccounts.length === 0) {
        return null;
    }
    const sortedAccounts = sortBy(filteredAccounts, (account) => account.Name);
    const title = (<div className={styles.typeHeader}>
            <div className={styles.typeHeaderTitleContainer}>{groupName}</div>
        </div>);
    return (<>
            <SectionHeader title={title}/>
            <List<AccountResource> items={sortedAccounts} renderRow={({ item: account }) => <AccountsListItem spaceId={spaceId} account={account} environments={environments} tenants={tenants}/>}/>
        </>);
}
function AccountsListItem({ spaceId, account, environments, tenants }: {
    spaceId: string;
    account: AccountResource;
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
}) {
    return (<ListItemLink href={links.accountPage.generateUrl({ spaceId, accountId: account.Id })}>
            <ListItemContent>
                <ListTitle>{account.Name}</ListTitle>
                {environmentV2ChipListIncludingMissing(environments, account.EnvironmentIds)}
                {tenantChipListIncludingMissing(tenants, account.TenantIds)}
                <MarkdownDescription markup={account.Description}/>
                {/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
                {account.AccountType === AccountType.AzureSubscription && !!(account as AzureSubscriptionAccountResource).CertificateThumbprint && (<Callout type={"danger"} title={"Azure Management Certificate accounts are not supported."}>
                        Azure Cloud Services have been retired by Microsoft. This resource will be removed in the next Octopus Release.
                    </Callout>)}
            </ListItemContent>
        </ListItemLink>);
}
function AddAccountButton({ spaceId }: {
    spaceId: string;
}) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const trackEvent = useTrackEvent();
    const addAccountItems: SimpleMenuItem[] = RawAccountTypeDetailsMap.map((accountTypeDetails) => ({
        type: "internal-link",
        label: accountTypeDetails.name,
        path: links.addAccountPage.generateUrl({ spaceId }, { accountType: accountTypeDetails.types[0] }),
        onClick: () => {
            trackEvent(createAddAccountAnalyticsEvent({ "Account Type": accountTypeDetails.types[0], Location: "Page" }));
        },
    }));
    return (<>
            <Button importance="primary" label="Add Account" onClick={openMenu} {...buttonAriaAttributes}/>
            <SimpleMenu menuState={menuState} items={addAccountItems} accessibleName={"Add account"} compact={true}/>
        </>);
}
// Custom bordered content so that we dont get double borders with the form headers at the top
export function BorderedContent({ children }: {
    children: React.ReactNode;
}) {
    return <div className={borderedContentStyles}>{children}</div>;
}
const borderedContentStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderTop: "none",
    borderRadius: borderRadius.medium,
    overflow: "clip",
});
